<template>
  <section>
    <f-data-table :id="tableId"
                  :data-uri="data_uri"
                  :server-side="tbl_config.server_side"
                  :columns="tbl_config.columns"
                  :heading="tbl_config.heading"
                  :icon="tbl_config.icon"
                  :static-params="tbl_config.static_params"
                  :selectable-rows="true">
      <!-- FILTERS -->
      <template #_filters>
        <f-data-table-filters :target="tableId" :form-columns="1"
                              filter-layout="col"
                              :expanded="false"
                              :filters="tbl_config.filters">
          <template #name="props">
            <f-data-table-filter field="name" display="Name:"
                                 :scope="props" />
          </template>
          <template #used_by="props">
            <f-data-table-filter field="used_by">
              <label for="tags-search">Filter by usage:</label>
              <multiselect v-model="used_by_filter_multi_select_val"
                           name="used_or_unused_that_is_the_question"
                           :options="used_by_filter_ms_options"
                           label="display"
                           track-by="val"
                           @input="(selected) => {
                             props.filterChange({
                               server: {
                                 type: 'param',
                                 field: 'type',
                                 value: selected.val,
                               }
                             });
                           }" />
            </f-data-table-filter>
          </template>
        </f-data-table-filters>
      </template>
      <!-- ACTIONS -->
      <template #_actions="props">
        <button type="button"
                class="btn btn-ss btn-icon-sm"
                @click="props.reloadData">
          <i class="flaticon2-refresh" />Reload Data
        </button>

        <button v-show="selected_tags && selected_tags.length"
                style="margin-left: 7px;"
                class="btn btn-ss-danger btn-icon-sm"
                type="button"
                @click="props.do({cb: checkDestroyTags})">
          <i class="flaticon2-warning" />
          Delete
        </button>
      </template>
      <!-- COLUMNS -->
      <template #name="props">
        <active-passive>
          <template #ap-passive>
            {{ props.value }}
          </template>
          <template #ap-active>
            <b-form-input :value="props.value"
                          @change="ev => props.do({
                            cb: () => quickEditItem(props.row, `name`, ev)
                          })" />
          </template>
        </active-passive>
      </template>
      <template #created_at="props">
        {{ formatDateTime(props.value) }}
      </template>
      <template #admin_only="props">
        <b-form-checkbox :checked="props.value"
                         @change="ev => props.do({
                           reload_data: false,
                           cb: () => quickEditItem(props.row, `admin_only`, ev)
                         })" />
      </template>
      <template #used_by="props">
        <span v-if="props.row.artworks_count">{{ formatPlural(props.row.artworks_count, 'Artwork') }}</span>
        <span v-if="props.row.feeds_count">{{ formatPlural(props.row.feeds_count, 'Feed') }}</span>
        <span v-if="props.row.items_count">{{ formatPlural(props.row.items_count, 'Item') }}</span>
      </template>

      <template #actions="props">
        <button type="button" class="btn btn-sm btn-ss-danger" @click="props.do({ cb: () => checkDestroyTags(props.row) })">
          <i class="la la-trash" /> Delete
        </button>
      </template>
    </f-data-table>
    <delete-confirm-modal name="delete-confirm" :entities-to-delete="selected_tags" entity-type="tag" />
  </section>
</template>

<script>
import {
  BFormInput,
  BFormCheckbox,
} from 'bootstrap-vue';
import FDataTable from "VuePartials/App/Storage/FDataTable/FDataTable.vue";
import FDataTableFilters from "VuePartials/App/Storage/FDataTable/FDataTableFilters.vue";
import FDataTableFilter from "VuePartials/App/Storage/FDataTable/FDataTableFilter.vue";
import ActivePassive from 'VueComponents/ActivePassive';
import DeleteConfirmModal from 'VueModals/DeleteConfirmModal';
import { doBulkAjax } from 'Lib/ajax-util';

import { destroyTags } from 'Lib/tags';

export default {
  components: {
    FDataTable,
    FDataTableFilters,
    FDataTableFilter,
    ActivePassive,
    DeleteConfirmModal,
    BFormInput,
    BFormCheckbox,
  },
  props: {
    tableId: {
      type: String,
      required: false,
      default: `tags-table`,
    },
  },
  data: () => ({
    selected_tags: [],
    used_by_filter_multi_select_val: {
      display: 'Show All',
      val: null,
    },
    used_by_filter_ms_options: [
      { display: 'Show All', val: null },
      { display: 'Unused', val: 'unused' },
      { display: 'Used', val: 'used' },
      { display: 'Feeds', val: 'feeds' },
      { display: 'Items', val: 'items' },
      { display: 'Artwork', val: 'artworks' },
    ],

  }),
  computed: {
    data_uri() {
      return `/api/v1/tags`;
    },
    tbl_config() {
      return {
        server_side: true,
        heading: `Manage Tags`,
        icon: `la la-tags`,
        static_params: [
          {
            field: `full`,
            value: true,
          },
        ],
        columns: [
          {
            field: `name`,
            display: `Name`,
          },
          {
            field: `created_at`,
            display: `Created At`,
            active_sort: true,
          },
          {
            field: `admin_only`,
            display: `Admin Only?`,
          },
          {
            field: `used_by`,
            display: `Used By`,
            sortable: false,
          },
          {
            field: `actions`,
            display: `Actions`,
            sortable: false,
            width: 150,
          },
        ],
      };
    },
  },
  mounted() {
    EventBus.$on(`fdt-rows-selected`, (ev) => {
      this.selected_tags = ev;
    });
  },
  methods: {
    doBulkAjax,
    // TODO: this type of fn should go in a mixin
    handleResponse(res, success_message, error_message = `Error`) {
      if (res.data && res.data.success) {
        toastr.success(success_message);
      } else {
        if (res.data.error.message) {
          error_message = `${error_message}: ${res.data.error.message}`;
        }
        toastr.error(_.trim(error_message) || `Something went wrong!`);
      }
    },
    // TODO: this type of fn should go in a mixin
    async quickEditItem(tag, field, value) {
      try {
        window.EventBus.$emit(`${this.tableId}-update-row`, {
          field,
          value,
          find: row => row.uuid === tag.uuid,
        });
        const res = await axios.patch(`${this.data_uri}/${tag.uuid}`, {
          [field]: value,
        });
        this.handleResponse(res, `Successfully updated tag '${tag.name}'!`, `Unable to update tag '${tag.name}'`);
      } catch (err) {
        window.handleAxiosError(err);
      }
      return false;
    },

    async checkDestroyTags(...tags) {
      const modal_res = await this.fireModal(`delete-confirm`);
      if (!modal_res.data.confirm) {
        return false;
      }
      return destroyTags(...tags);
    },
    formatPlural(count, singular) {
      return `${count} ${singular}${count === 1 ? '' : 's'}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.entity-cat {
  margin-top: 0.2em;
  margin-bottom: 0.2em;
}
</style>
